import React, { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";

const Header = ({
  query,
  setQuery,
  orientation,
  setOrientation,
  onSearch,
  isLoading,
  isPanelOpen,
  togglePanel,
  closePanel,
  user,
  isAdmin,
  isAuthPanelOpen,
  toggleAuthPanel,
  closeAuthPanel,
  handleSignIn,
  handleSignUp,
  handleSignOut,
  email,
  setEmail,
  password,
  setPassword,
  name,
  setName,
  isSignUpMode,
  setIsSignUpMode,
  authError,
  setError,
  showCollection,
  setShowCollection,
  collections,
  activeCollection,
  setActiveCollection,
  createCollection,
  deleteCollection,
  sharedCollections,
  fetchSharedCollection,
  shareCollection,
  unshareCollection,
  resetToSearchView,
}) => {
  const [newCollectionName, setNewCollectionName] = useState("");
  const [isMessageDismissed, setIsMessageDismissed] = useState(false); // State to track if the message is dismissed
  const canViewCollection = collections.length > 0 && activeCollection;

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError("Please enter your email address to reset your password.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setError("Password reset email sent! Please check your inbox.");
      setTimeout(() => setError(null), 5000); // Clear the message after 5 seconds
    } catch (err) {
      if (err.code === "auth/user-not-found") {
        setError("No user found with this email address.");
      } else if (err.code === "auth/invalid-email") {
        setError("Invalid email address.");
      } else {
        setError(`Failed to send password reset email: ${err.message}`);
      }
      console.error("Password Reset Error:", err);
    }
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-md sticky top-0 z-10">
      {/* Dismissible Message for Non-Logged-In Users */}
      {!user && !isMessageDismissed && (
        <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 dark:hover:text-blue-100 p-3 text-center relative">
          <p className="text-sm cursor-pointer" onClick={toggleAuthPanel}>
            Sign In to create and save your own custom photo collections!
          </p>
          <button
            onClick={() => setIsMessageDismissed(true)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 focus:outline-none"
            aria-label="Close message"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
      )}

      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <h1
          className="text-2xl font-bold text-gray-600 dark:text-gray-300 cursor-pointer"
          onClick={resetToSearchView}
        >
          Image Search{" "}
          <span className="block md:inline font-semibold text-sm">
            Timed Slider for Artists
          </span>
        </h1>

        <div className="flex items-center gap-4">
          <button
            onClick={toggleAuthPanel}
            className="p-2 focus:outline-none hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
            aria-label="Toggle authentication panel"
          >
            <svg
              className="w-6 h-6 text-gray-600 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              ></path>
            </svg>
          </button>
          <button
            onClick={togglePanel}
            className="p-2 focus:outline-none hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
            aria-label="Toggle search panel"
          >
            <svg
              className="w-5 h-6 fill-gray-600 dark:fill-gray-300"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
            >
              <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" />
            </svg>
          </button>
        </div>

        {/* Search Panel */}
        <div
          className={`fixed z-[100] top-0 right-0 h-full w-80 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out overflow-auto ${
            isPanelOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="p-6 flex flex-col h-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Search Options
              </h2>
              <button
                onClick={closePanel}
                className="p-1 focus:outline-none hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                aria-label="Close search panel"
              >
                <svg
                  className="w-5 h-5 text-gray-600 dark:text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>
            <div className="flex flex-col gap-4 flex-grow pb-6">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Adobe Stock..."
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    onSearch(true);
                    closePanel();
                  }
                }}
              />
              <select
                value={orientation}
                onChange={(e) => setOrientation(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="horizontal">Horizontal</option>
                <option value="vertical">Vertical</option>
                <option value="square">Square</option>
              </select>
              <button
                onClick={() => {
                  onSearch(true);
                  closePanel();
                }}
                disabled={isLoading}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
              >
                {isLoading ? "Searching..." : "Search"}
              </button>

              {sharedCollections.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                    Example Collections
                  </h3>
                  <ul className="space-y-2">
                    {sharedCollections.map((sharedCol) => (
                      <li
                        key={sharedCol.id}
                        className="flex justify-between items-center p-2 bg-gray-200 dark:bg-gray-700 rounded-lg"
                      >
                        <span>{sharedCol.collectionName}</span>
                        <button
                          onClick={() => {
                            fetchSharedCollection(sharedCol);
                            closePanel();
                          }}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs"
                        >
                          View
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Auth Panel */}
        <div
          className={`fixed z-[100] top-0 right-0 h-full w-80 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out overflow-auto ${
            isAuthPanelOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="p-6 flex flex-col h-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {user ? "Account" : isSignUpMode ? "Sign Up" : "Sign In"}
              </h2>
              <button
                onClick={closeAuthPanel}
                className="p-1 focus:outline-none hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                aria-label="Close auth panel"
              >
                <svg
                  className="w-5 h-5 text-gray-600 dark:text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>

            {user ? (
              <div className="flex flex-col gap-4 flex-grow pb-6">
                <p className="text-gray-900 dark:text-white">
                  Welcome, {user.displayName || user.email}{" "}
                  {isAdmin && <span className="text-blue-500">(Admin)</span>}
                </p>
                <button
                  onClick={() => setShowCollection(true)}
                  disabled={!canViewCollection}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  View Collection
                </button>
                <select
                  value={activeCollection}
                  onChange={(e) => setActiveCollection(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {collections.map((col) => (
                    <option key={col} value={col}>
                      {col}
                    </option>
                  ))}
                </select>
                <div className="flex flex-col gap-2">
                  {collections.map((col) => {
                    const sharedCollectionId = `${user.uid}_${col}`;
                    const isShared = sharedCollections.some(
                      (sharedCol) => sharedCol.id === sharedCollectionId
                    );
                    return (
                      <div
                        key={col}
                        className="flex justify-between items-center p-2 bg-gray-200 dark:bg-gray-700 rounded-lg"
                      >
                        <span>{col}</span>
                        <div className="flex gap-2">
                          {isAdmin && (
                            <button
                              onClick={() =>
                                isShared
                                  ? unshareCollection(sharedCollectionId)
                                  : shareCollection(col)
                              }
                              className={`px-3 py-1 rounded-lg text-white text-xs ${
                                isShared
                                  ? "bg-red-600 hover:bg-red-700"
                                  : "bg-green-600 hover:bg-green-700"
                              }`}
                            >
                              {isShared ? "Unshare" : "Share"}
                            </button>
                          )}
                          {col !== "Favorites" && (
                            <button
                              onClick={() => deleteCollection(col)}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    placeholder="New collection name"
                    className="flex-grow px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => {
                      createCollection(newCollectionName);
                      setNewCollectionName("");
                    }}
                    disabled={!newCollectionName.trim()}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
                <button
                  onClick={handleSignOut}
                  className="mt-auto px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-4 flex-grow pb-6">
                {authError && (
                  <p
                    role="alert"
                    className={`text-sm ${
                      authError.includes("Password reset email sent")
                        ? "text-green-500"
                        : "text-red-500"
                    }`}
                  >
                    {authError}
                  </p>
                )}
                {isSignUpMode && (
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={isSignUpMode ? handleSignUp : handleSignIn}
                  disabled={isLoading}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
                >
                  {isLoading
                    ? "Processing..."
                    : isSignUpMode
                    ? "Sign Up"
                    : "Sign In"}
                </button>
                {!isSignUpMode && (
                  <button
                    onClick={handleForgotPassword}
                    className="text-blue-500 hover:underline focus:outline-none text-sm"
                  >
                    Forgot Password?
                  </button>
                )}
                <button
                  onClick={() => setIsSignUpMode(!isSignUpMode)}
                  className="text-blue-500 hover:underline focus:outline-none"
                >
                  {isSignUpMode
                    ? "Already have an account? Sign In"
                    : "Need an account? Sign Up"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
